import type { FC } from "react";
import React, { useState, useMemo } from "react";

import { translate } from "app/filters/i18nFilter";
import GenericWidgetTable from "app/pages/website-analysis/traffic-sources/search/overview/components/GenericWidgetTable/GenericWidgetTable";
import { isCompareMode } from "app/UtilitiesAndConstants/UtilityFunctions/isCompareMode";

import SearchTableTop from "./components/SearchTableTop/SearchTableTop";
import type { SortConfig } from "./tableColumns";
import { getLobTableColumns } from "./tableColumns";
import useLobDataTable from "./useLobDataTable";

const DEFAULT_SORT_CONFIG: SortConfig = {
    key: "totalVisits",
    ascending: false,
};

const LineOfBusinessTable: FC = () => {
    const isCompare = isCompareMode();
    const { data, isLoading, isError } = useLobDataTable(isCompare);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState<SortConfig>(DEFAULT_SORT_CONFIG);

    const onSort = (sorter) => {
        setSortConfig({
            key: sorter.columnKey as string,
            ascending: sorter.order === "ascend",
        });
    };
    const onSearch = (value: string) => {
        setSearchTerm(value.trim());
    };

    const filteredData = useMemo(() => {
        if (!searchTerm) {
            return data;
        }
        const term = searchTerm.toLowerCase();
        return data.filter(({ lobName }) => lobName.toLowerCase().includes(term));
    }, [data, searchTerm]);

    const columnConfig = useMemo(
        () => (isLoading || !filteredData.length ? [] : getLobTableColumns(filteredData.length, sortConfig, isCompare)),
        [filteredData.length, isLoading, sortConfig, isCompare],
    );
    if (!isLoading && data.length <= 1) {
        return null;
    }

    return (
        <GenericWidgetTable
            data-automation="line-of-business-table"
            title={translate("analysis.content.business.line.segments.title")}
            tooltip={translate("analysis.content.business.line.segments.tooltip")}
            TableTop={<SearchTableTop onChange={onSearch} />}
            isError={isError}
            tableProps={{
                data: filteredData,
                columnConfig,
                isLoading,
                bordersConfig: { body: false, groupBorder: true, header: true },
                theme: "gray",
                rowHoverable: true,
                onSort,
                pagination: {
                    pageSize: 20,
                },
            }}
        />
    );
};

export default React.memo(LineOfBusinessTable);
